import React, { useEffect, useState } from 'react';
import { Trophy, Loader2, Star, Flame, TrendingUp } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface GamificationStats {
  totalXP: number;
  avgLevel: number;
  avgStreak: number;
  maxLevel: number;
  maxStreak: number;
  levelDistribution: { level: number; count: number }[];
}

export const GamificationPage: React.FC = () => {
  const [stats, setStats] = useState<GamificationStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data: users } = await adminDb.from('users').select('data');
      const userList = users || [];

      let totalXP = 0, totalLevel = 0, totalStreak = 0, maxLevel = 0, maxStreak = 0;
      const levelBuckets: Record<number, number> = {};

      userList.forEach(u => {
        const xp = u.data?.xp || 0;
        const level = u.data?.level || 0;
        const streak = u.data?.streak || 0;
        totalXP += xp;
        totalLevel += level;
        totalStreak += streak;
        maxLevel = Math.max(maxLevel, level);
        maxStreak = Math.max(maxStreak, streak);
        levelBuckets[level] = (levelBuckets[level] || 0) + 1;
      });

      const levelDistribution = Object.entries(levelBuckets)
        .map(([level, count]) => ({ level: parseInt(level), count }))
        .sort((a, b) => a.level - b.level);

      setStats({
        totalXP,
        avgLevel: userList.length > 0 ? Math.round(totalLevel / userList.length) : 0,
        avgStreak: userList.length > 0 ? Math.round(totalStreak / userList.length * 10) / 10 : 0,
        maxLevel,
        maxStreak,
        levelDistribution,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div>
        <div className="page-header"><h1><Trophy size={24} /> Gamification</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header"><h1><Trophy size={24} /> Gamification</h1></div>

      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Gesamt XP vergeben</span>
          <span className="kpi-value">{stats.totalXP.toLocaleString('de-DE')}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ø Level</span>
          <span className="kpi-value">{stats.avgLevel}</span>
          <span className="text-xs text-muted">Max: {stats.maxLevel}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ø Streak</span>
          <span className="kpi-value">{stats.avgStreak}</span>
          <span className="text-xs text-muted">Max: {stats.maxStreak} Tage</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Level-Verteilung</span>
          <span className="kpi-value">{stats.levelDistribution.length}</span>
          <span className="text-xs text-muted">verschiedene Level</span>
        </div>
      </div>

      {/* Level Distribution */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 16 }}><Star size={18} /> Level-Verteilung</h3>
        {stats.levelDistribution.length === 0 ? (
          <p className="text-muted text-sm">Keine Daten vorhanden.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {stats.levelDistribution.map(item => {
              const maxCount = Math.max(...stats.levelDistribution.map(d => d.count));
              return (
                <div key={item.level} className="flex items-center gap-3">
                  <span className="text-sm" style={{ width: 70, flexShrink: 0 }}>Level {item.level}</span>
                  <div style={{ flex: 1, background: 'var(--border-light)', borderRadius: 4, height: 24, position: 'relative', overflow: 'hidden' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, var(--primary), var(--primary-hover))',
                      height: '100%', borderRadius: 4,
                      width: `${(item.count / maxCount) * 100}%`,
                      transition: 'width 0.5s ease',
                    }} />
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 600 }}>
                      {item.count} Nutzer
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* XP System Info */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}><TrendingUp size={18} /> XP-System</h3>
        <div className="table-container" style={{ margin: 0 }}>
          <table>
            <thead><tr><th>Aktion</th><th>XP</th></tr></thead>
            <tbody>
              <tr><td>Frage korrekt beantwortet</td><td className="text-mono">+10 XP</td></tr>
              <tr><td>Frage falsch beantwortet</td><td className="text-mono">+3 XP</td></tr>
              <tr><td>Kapitel abgeschlossen</td><td className="text-mono">+50 XP</td></tr>
              <tr><td>Prüfungssimulation bestanden</td><td className="text-mono">+100 XP</td></tr>
              <tr><td>Streak 7 Tage</td><td className="text-mono">+25 XP</td></tr>
              <tr><td>Streak 30 Tage</td><td className="text-mono">+100 XP</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
